import React, { useState, useEffect } from 'react';
import { auth, db } from '../lib/firebase';
import { doc, updateDoc } from 'firebase/firestore';
import { Link, useNavigate } from 'react-router-dom';
import {
  Sparkles,
  ShieldCheck,
  CheckCircle2,
  AlertTriangle,
  Clock,
  Heart,
  Calendar,
  Phone,
  Mail,
  MapPin,
  DollarSign,
  Info,
  Check,
  ChevronRight,
  LogOut,
  User,
  Users,
  Settings,
  Bell,
  MessageSquare,
  CreditCard,
  Camera,
  Edit3,
  HelpCircle,
  Eye,
  Send,
  X,
  ShieldAlert,
  Save,
  RotateCcw,
  Zap,
  MoreVertical,
  KeyRound,
  ExternalLink,
  Car,
  Compass,
  AlertCircle,
  IndianRupee,
  FileCheck,
  FileText,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { PairUpLogo } from '../components/common/PairUpLogo';
import { fetchPairings, payPairingSide, PairingItem, verifyPaymentWithBackend } from '../lib/paymentService';
import { BookingRecord, PayoutRecord } from '../types';
import {
  fetchV2Bookings,
  acceptV2Booking,
  declineV2Booking,
  checkInV2Booking,
  sendMeetingStartOtp,
  verifyMeetingStartOtp,
  getBookingCompletionOtp,
  verifyBookingCompletionOtp,
  fetchCompanionPayoutStats,
} from '../lib/bookingService';
import { BookingChatModal } from '../components/chat/BookingChatModal';
import { realtimeSocket } from '../lib/socket';
import { CompanionLifecycleManager } from '../components/dashboard/CompanionLifecycleManager';
import { CompanionVerificationCenter } from '../components/verification/CompanionVerificationCenter';
import { CompanionProfileStatusIndicator } from '../components/dashboard/CompanionProfileStatusIndicator';
import { ProfileStatusBadge } from '../components/dashboard/ProfileStatusBadge';
import { DocumentStatusSection } from '../components/dashboard/DocumentStatusSection';
import { updateCompanionProfileFlow } from '../utils/companionStatusManager';

export const CompanionDashboardPage: React.FC = () => {
  const { currentUser, userProfile, updateProfile, logout } = useAuth();
  const navigate = useNavigate();

  // Active section among the SRD sections
  const [activeSection, setActiveSection] = useState<
    | 'dashboard'
    | 'document_status'
    | 'profile'
    | 'lifecycle'
    | 'verification'
    | 'requests'
    | 'matches'
    | 'messages'
    | 'availability'
    | 'payments'
    | 'notifications'
    | 'settings'
    | 'support'
  >('dashboard');

  // V2 Launch Architecture Bookings & Payout State
  const [v2Bookings, setV2Bookings] = useState<BookingRecord[]>([]);
  const [payoutStats, setPayoutStats] = useState<{
    availableBalance: number;
    pendingBalance: number;
    totalEarned: number;
    totalPlatformFees: number;
    totalPaidOut: number;
    payouts: PayoutRecord[];
  }>({
    availableBalance: 0,
    pendingBalance: 0,
    totalEarned: 0,
    totalPlatformFees: 0,
    totalPaidOut: 0,
    payouts: [],
  });
  const [activeChatBooking, setActiveChatBooking] = useState<BookingRecord | null>(null);
  const [activeStartOtpBooking, setActiveStartOtpBooking] = useState<BookingRecord | null>(null);
  const [startOtpInput, setStartOtpInput] = useState('');
  const [isVerifyingStartOtp, setIsVerifyingStartOtp] = useState(false);
  const [startOtpError, setStartOtpError] = useState('');
  const [startOtpNotice, setStartOtpNotice] = useState('');

  const [activeOtpBooking, setActiveOtpBooking] = useState<BookingRecord | null>(null);
  const [slideToCompleteProgress, setSlideToCompleteProgress] = useState<number>(0);
  const [slideToCompleteConfirmed, setSlideToCompleteConfirmed] = useState<boolean>(false);
  const [completionOtpInput, setCompletionOtpInput] = useState('');
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  const [otpVerifyError, setOtpVerifyError] = useState('');
  const [otpVerifySuccess, setOtpVerifySuccess] = useState<{ amount: number } | null>(null);
  const [nowTick, setNowTick] = useState<number>(Date.now());

  useEffect(() => {
    const timer = setInterval(() => setNowTick(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);

  const getBookingRemainingCountdown = (b: BookingRecord) => {
    const startedAt = b.meeting_started_at;
    const countdownEnds = (b as any).countdown_ends_at;
    if (!startedAt && !countdownEnds) return null;
    const endMs = countdownEnds
      ? new Date(countdownEnds).getTime()
      : new Date(startedAt!).getTime() + (b.duration_hours || 2) * 3600 * 1000;
    const diffSec = Math.max(0, Math.floor((endMs - nowTick) / 1000));
    const hrs = Math.floor(diffSec / 3600);
    const mins = Math.floor((diffSec % 3600) / 60);
    const secs = diffSec % 60;
    return `${String(hrs).padStart(2, '0')}:${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  };

  // Pairings / Dual-Payment Requests
  const [pairings, setPairings] = useState<PairingItem[]>([]);
  const [isLoadingPairings, setIsLoadingPairings] = useState(false);
  const [actionNotice, setActionNotice] = useState<{ message: string; type: 'success' | 'error' | 'info' } | null>(null);

  // Profile Edit form state
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [editName, setEditName] = useState(userProfile?.displayName || userProfile?.name || 'Companion');
  const [editBio, setEditBio] = useState(userProfile?.bio || '');
  const [editRate, setEditRate] = useState(userProfile?.hourlyRate || 1500);
  const [editRateOneThirdDay, setEditRateOneThirdDay] = useState(userProfile?.rateOneThirdDay || 2000);
  const [editRateHalfDay, setEditRateHalfDay] = useState(userProfile?.rateHalfDay || 3000);
  const [editRateFullDay, setEditRateFullDay] = useState(userProfile?.rateFullDay || 5000);
  const [editCity, setEditCity] = useState(userProfile?.city || 'Kolkata, West Bengal');

  // Service categories selection
  const [selectedServices, setSelectedServices] = useState<string[]>(
    userProfile?.companionProfile?.services ||
    (userProfile?.services && userProfile.services.length > 0 ? userProfile.services.map(s => s.name) : ['Dinner', 'Tourist Guide'])
  );

  // Driver verification fields
  const [dlNumber, setDlNumber] = useState(
    userProfile?.drivingLicenceDetails?.licenceNumber ||
    userProfile?.companionProfile?.drivingLicence?.licenceNumber ||
    ''
  );
  const [dlExpiry, setDlExpiry] = useState(
    userProfile?.drivingLicenceDetails?.expiryDate ||
    userProfile?.companionProfile?.drivingLicence?.expiryDate ||
    '2030-12-31'
  );
  const [dlFront, setDlFront] = useState(
    userProfile?.drivingLicenceDetails?.frontUrl ||
    userProfile?.companionProfile?.drivingLicence?.frontUrl ||
    'https://images.unsplash.com/photo-1557683316-973673baf926?auto=format&fit=crop&w=800&q=80'
  );
  const [dlBack, setDlBack] = useState(
    userProfile?.drivingLicenceDetails?.backUrl ||
    userProfile?.companionProfile?.drivingLicence?.backUrl ||
    'https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?auto=format&fit=crop&w=800&q=80'
  );
  const [drivingLicenceStatus, setDrivingLicenceStatus] = useState<string>(
    userProfile?.drivingLicenceStatus ||
    userProfile?.companionProfile?.drivingLicence?.status ||
    (userProfile?.isDriverApproved ? 'APPROVED' : 'NOT_UPLOADED')
  );
  const [dlRejectionReason, setDlRejectionReason] = useState<string>(
    userProfile?.drivingLicenceDetails?.rejectionReason ||
    userProfile?.companionProfile?.drivingLicence?.rejectionReason ||
    ''
  );
  const [isSubmittingDl, setIsSubmittingDl] = useState(false);

  // Availability state
  const [workingDays, setWorkingDays] = useState({
    Mon: true,
    Tue: true,
    Wed: true,
    Thu: true,
    Fri: true,
    Sat: true,
    Sun: false,
  });
  const [workingHours, setWorkingHours] = useState('11:00 AM - 10:00 PM');

  // Interactive Chat State
  const [chatMessages, setChatMessages] = useState<{ id: string; sender: 'me' | 'client'; text: string; time: string }[]>([
    {
      id: 'm1',
      sender: 'client',
      text: 'Hello! I booked our dinner pairing for tomorrow at Park Street. Looking forward to meeting you!',
      time: '14:20',
    },
    {
      id: 'm2',
      sender: 'me',
      text: 'Hi Vikram! Sounds wonderful. I have confirmed my pairing fee on the system as well. See you there!',
      time: '14:25',
    },
  ]);
  const [newMsgText, setNewMsgText] = useState('');

  // Settings
  const [profileVisibility, setProfileVisibility] = useState<'Public' | 'Limited' | 'Hidden'>('Public');

  // Verification status
  const verificationStatus = userProfile?.isIdVerified ? 'ACTIVE' : (userProfile as any)?.accountStatus === 'Suspended' ? 'SUSPENDED' : 'PENDING_VERIFICATION';

  const showToast = (message: string, type: 'success' | 'error' | 'info' = 'success') => {
    setActionNotice({ message, type });
    setTimeout(() => setActionNotice(null), 4000);
  };

  // Section 25: Companion Active/Inactive availability state & toggle
  const isCompanionAvailable = Boolean(userProfile?.isAvailable ?? ((userProfile as any)?.companion_profile_status === 'Active'));
  const [isTogglingAvailability, setIsTogglingAvailability] = useState(false);

  const handleToggleAvailability = async () => {
    if (isTogglingAvailability) return;
    const isSuspended = (userProfile as any)?.accountStatus === 'Suspended' || (userProfile as any)?.isSuspended;
    if (isSuspended) {
      showToast('Account is suspended. You cannot set status to Active.', 'error');
      return;
    }
    const newStatus = !isCompanionAvailable;
    setIsTogglingAvailability(true);
    try {
      await updateProfile({
        isAvailable: newStatus,
        companion_profile_status: newStatus ? 'Active' : 'Inactive',
      } as any);

      const companionId = (userProfile as any)?.uid || auth.currentUser?.uid;
      if (companionId && db) {
        try {
          await updateDoc(doc(db, 'companions', companionId), {
            isAvailable: newStatus,
            companion_profile_status: newStatus ? 'Active' : 'Inactive',
            updatedAt: new Date().toISOString(),
          });
        } catch {
          // companions doc update fallback
        }
      }

      showToast(
        newStatus
          ? 'Status updated: You are now ACTIVE and discoverable for client bookings!'
          : 'Status updated: You are now INACTIVE. New bookings are paused.',
        'success'
      );
    } catch (e: any) {
      showToast(e.message || 'Failed to update availability status', 'error');
    } finally {
      setIsTogglingAvailability(false);
    }
  };

  const loadPairings = async () => {
    setIsLoadingPairings(true);
    try {
      const res = await fetchPairings(undefined, userProfile?.email || 'comp');
      if (res.success && Array.isArray(res.pairings)) {
        setPairings(res.pairings);
      }
    } catch {
      // fallback
    } finally {
      setIsLoadingPairings(false);
    }
  };

  const loadV2Data = async () => {
    try {
      const companionUid = auth.currentUser?.uid;
      if (!companionUid) {
        console.warn('Companion data requires an authenticated Firebase user.');
        return;
      }
      const [bookingsRes, payoutRes] = await Promise.all([
        fetchV2Bookings(),
        fetchCompanionPayoutStats(companionUid)
      ]);
      if (bookingsRes.success && Array.isArray(bookingsRes.bookings)) {
        setV2Bookings(bookingsRes.bookings);
      }
      if (payoutRes.success) {
        setPayoutStats(payoutRes);
      }
    } catch (e) {
      console.warn('Failed to load V2 companion bookings:', e);
    }
  };

  useEffect(() => {
    loadPairings();
    loadV2Data();

    const unsubStatus = realtimeSocket.subscribe('BOOKING_STATUS_CHANGED', () => {
      loadV2Data();
    });
    const unsubOtp = realtimeSocket.subscribe('OTP_VERIFIED', () => {
      loadV2Data();
    });
    const unsubNotif = realtimeSocket.subscribe('NOTIFICATION', (notif: any) => {
      if (notif?.title) {
        showToast(notif.title, 'info');
      }
      loadV2Data();
    });

    return () => {
      unsubStatus();
      unsubOtp();
      unsubNotif();
    };
  }, []);

  const handleAcceptBooking = async (bookingId: string) => {
    const companionUid = auth.currentUser?.uid;
    if (!companionUid) {
      showToast('Please sign in again to continue.', 'error');
      return;
    }
    try {
      const res = await acceptV2Booking(bookingId, companionUid);
      if (res.success) {
        showToast('Booking accepted! Private chat is now enabled with the client.', 'success');
        loadV2Data();
      } else {
        showToast(res.error || 'Failed to accept booking', 'error');
      }
    } catch (err: any) {
      showToast(err.message || 'Error accepting booking', 'error');
    }
  };

  const handleDeclineBooking = async (bookingId: string) => {
    const companionUid = auth.currentUser?.uid;
    if (!companionUid) {
      showToast('Please sign in again to continue.', 'error');
      return;
    }
    try {
      const res = await declineV2Booking(bookingId, companionUid, 'Companion schedule conflict');
      if (res.success) {
        showToast('Booking declined. Client has been automatically refunded.', 'info');
        loadV2Data();
      }
    } catch (err: any) {
      showToast(err.message || 'Error declining booking', 'error');
    }
  };

  const handleCheckInBooking = async (bookingId: string) => {
    try {
      const res = await checkInV2Booking(bookingId, 'companion');
      if (res.success) {
        showToast("Checked in! Client notified you are at the location.", 'success');
        loadV2Data();
      }
    } catch (err: any) {
      showToast(err.message || 'Check-in failed', 'error');
    }
  };

  const handleOpenStartMeetingModal = async (booking: BookingRecord) => {
    setActiveStartOtpBooking(booking);
    setStartOtpInput('');
    setStartOtpError('');
    setStartOtpNotice('Sending Meeting Start OTP via APITXT SMS to the client...');
    const res = await sendMeetingStartOtp(booking.id);
    if (res.success) {
      setStartOtpNotice(
        res.message || "Meeting Start OTP sent via APITXT SMS to the client's registered mobile number."
      );
    } else {
      setStartOtpError(res.error || 'Could not dispatch Meeting Start OTP.');
      setStartOtpNotice('');
    }
  };

  const handleVerifyStartOtpSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!activeStartOtpBooking || !startOtpInput.trim()) return;
    setStartOtpError('');
    setIsVerifyingStartOtp(true);
    try {
      const res = await verifyMeetingStartOtp(activeStartOtpBooking.id, startOtpInput.trim());
      if (res.success) {
        showToast('Meeting Start OTP verified! Meeting countdown timer is now running.', 'success');
        setActiveStartOtpBooking(null);
        setStartOtpInput('');
        loadV2Data();
      } else {
        setStartOtpError(res.error || 'Invalid Meeting Start OTP.');
      }
    } catch (err: any) {
      setStartOtpError(err.message || 'Failed to verify Meeting Start OTP.');
    } finally {
      setIsVerifyingStartOtp(false);
    }
  };

  const handleSlideCompleteChange = async (value: number) => {
    setSlideToCompleteProgress(value);
    if (value >= 95 && !slideToCompleteConfirmed && activeOtpBooking) {
      setSlideToCompleteProgress(100);
      setSlideToCompleteConfirmed(true);
      const res = await getBookingCompletionOtp(activeOtpBooking.id);
      if (res.success) {
        showToast("Meeting Completion OTP sent via APITXT SMS to the client's mobile number.", 'info');
      } else if (res.error) {
        setOtpVerifyError(res.error);
      }
    }
  };

  const handleVerifyOtpSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!activeOtpBooking || !completionOtpInput.trim()) return;

    const companionUid = auth.currentUser?.uid;
    if (!companionUid) {
      setOtpVerifyError('Please sign in again before verifying the completion OTP.');
      return;
    }

    setOtpVerifyError('');
    setIsVerifyingOtp(true);

    try {
      const res = await verifyBookingCompletionOtp(
        activeOtpBooking.id,
        companionUid,
        completionOtpInput.trim()
      );

      if (res.success && res.payout) {
        setOtpVerifySuccess({ amount: res.payout.payout_amount });
        showToast(`✓ Meeting Successful! Booking Completed. ₹${res.payout.payout_amount} credited to companion wallet.`, 'success');
        loadV2Data();
      } else {
        setOtpVerifyError(res.error || 'Invalid OTP code. Please ask the client for their 6-digit completion code.');
      }
    } catch (err: any) {
      setOtpVerifyError(err.message || 'Failed to verify OTP');
    } finally {
      setIsVerifyingOtp(false);
    }
  };

  // Handle Companion completing their side of Dual Payment for Pairing
  const handlePayCompanionSide = async (pairId: string) => {
    try {
      const res = await payPairingSide(pairId, 'companion', 'UPI (GPay)');
      if (res.success) {
        showToast(res.message || 'Payment recorded!', 'success');
        loadPairings();
      } else {
        showToast(res.error || 'Payment failed', 'error');
      }
    } catch (err: any) {
      showToast(err.message || 'Payment error', 'error');
    }
  };

  const handleSendMessage = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMsgText.trim()) return;
    setChatMessages((prev) => [
      ...prev,
      {
        id: `msg-${Date.now()}`,
        sender: 'me',
        text: newMsgText.trim(),
        time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      },
    ]);
    setNewMsgText('');
  };

  const handleToggleService = (srv: string) => {
    setSelectedServices((prev) =>
      prev.includes(srv) ? prev.filter((s) => s !== srv) : [...prev, srv]
    );
  };

  const handleSaveProfile = async () => {
    try {
      const hRate = Number(editRate);
      if (hRate < 600) {
        showToast('Hourly rate must be at least ₹600 per platform policy.', 'error');
        return;
      }
      const oneThirdRate = Math.max(1000, Number(editRateOneThirdDay) || 2000);
      const halfRate = Math.max(1500, Number(editRateHalfDay) || 3000);
      const fullRate = Math.max(2500, Number(editRateFullDay) || 5000);

      let dlStatus = drivingLicenceStatus;
      let isApproved = userProfile?.isDriverApproved || dlStatus === 'APPROVED';

      // Driver category validation
      if (selectedServices.includes('Driver')) {
        if (!dlNumber.trim() || !dlFront.trim() || !dlBack.trim()) {
          showToast('Valid Driving Licence (number, front & back image) is mandatory for Driver service.', 'error');
          return;
        }

        // If newly submitted or re-uploaded after rejection
        if (dlStatus === 'NOT_UPLOADED' || dlStatus === 'REJECTED' || dlStatus === 'REUPLOAD_REQUIRED') {
          dlStatus = 'SUBMITTED';
          isApproved = false; // Driver service remains inactive until Admin approval
          setDrivingLicenceStatus('SUBMITTED');

          // Submit to KYC endpoint for admin verification
          try {
            await fetch('/api/v1/kyc/submit', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                userId: userProfile?.email || 'companion-user',
                userName: editName,
                userEmail: userProfile?.email || 'companion@pairuptoday.com',
                userRole: 'companion',
                documentType: 'DRIVING_LICENSE',
                drivingLicenceNumber: dlNumber.trim(),
                expiryDate: dlExpiry,
                frontImageUrl: dlFront.trim(),
                backImageUrl: dlBack.trim(),
                isDriverVerification: true,
              }),
            });
          } catch (err) {
            console.warn('KYC submission sync notice:', err);
          }
        }
      }

      await updateProfile({
        displayName: editName,
        bio: editBio,
        hourlyRate: hRate,
        rateOneThirdDay: oneThirdRate,
        rateHalfDay: halfRate,
        rateFullDay: fullRate,
        city: editCity,
        drivingLicenceStatus: dlStatus as any,
        isDriverApproved: isApproved,
        drivingLicenceDetails: {
          licenceNumber: dlNumber.trim(),
          expiryDate: dlExpiry,
          frontUrl: dlFront.trim(),
          backUrl: dlBack.trim(),
          submittedAt: new Date().toISOString(),
          rejectionReason: dlStatus === 'APPROVED' ? undefined : dlRejectionReason,
        },
        companionProfile: {
          ...(userProfile?.companionProfile as any),
          displayName: editName,
          aboutMe: editBio,
          hourlyRate: hRate,
          rateOneThirdDay: oneThirdRate,
          rateHalfDay: halfRate,
          rateFullDay: fullRate,
          city: editCity,
          services: selectedServices,
          isDriverApproved: isApproved,
          drivingLicence: {
            licenceNumber: dlNumber.trim(),
            expiryDate: dlExpiry,
            frontUrl: dlFront.trim(),
            backUrl: dlBack.trim(),
            status: dlStatus as any,
            rejectionReason: dlStatus === 'APPROVED' ? undefined : dlRejectionReason,
            submittedAt: new Date().toISOString(),
          },
        } as any,
      });

      // Synchronize companion profile and recalculate authoritative lifecycle status in Firestore
      const targetCompanionId = (userProfile as any)?.uid || (userProfile?.email ? userProfile.email.replace(/[^a-zA-Z0-9]/g, '_') : 'CP1024');
      if (targetCompanionId) {
        await updateCompanionProfileFlow(
          targetCompanionId,
          {
            name: editName,
            displayName: editName,
            bio: editBio,
            hourlyRate: hRate,
            rateOneThirdDay: oneThirdRate,
            rateHalfDay: halfRate,
            rateFullDay: fullRate,
            city: editCity,
            services: selectedServices,
            isDriverApproved: isApproved,
            isProfileInfoComplete: Boolean(editName && editBio),
          },
          { userId: (userProfile as any)?.uid }
        );
      }

      setIsEditingProfile(false);
      showToast('Profile, rates, and services saved successfully!', 'success');
    } catch (e: any) {
      showToast(e.message || 'Error saving profile', 'error');
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 flex flex-col md:flex-row">
      {/* ========================================================= */}
      {/* COMPANION SIDEBAR NAVIGATION */}
      {/* ========================================================= */}
      <aside className="w-full md:w-64 bg-slate-900 border-r border-slate-800 shrink-0 flex flex-col justify-between">
        <div className="p-5 space-y-6">
          {/* Logo & Portal Badge */}
          <div>
            <Link to="/" className="flex items-center gap-2.5">
              <PairUpLogo className="w-8 h-8 shrink-0 drop-shadow-sm" />
              <span className="font-black text-white text-base tracking-tight">
                PairUp<span className="text-purple-400">Today</span>
              </span>
            </Link>
            <div className="mt-2 text-[10px] uppercase font-mono font-bold tracking-wider px-2 py-0.5 rounded-md bg-purple-950 border border-purple-800 text-purple-300 inline-block">
              Companion Portal
            </div>
          </div>

          {/* Companion Profile Card in Sidebar */}
          <div className="p-3 bg-slate-950 rounded-2xl border border-slate-800 space-y-2">
            <div className="flex items-center gap-3">
              <img
                src={userProfile?.avatarUrl || 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&w=200&q=80'}
                alt={userProfile?.name}
                className="w-10 h-10 rounded-xl object-cover border border-purple-500/60"
              />
              <div className="overflow-hidden">
                <div className="font-bold text-white text-xs truncate flex items-center gap-1">
                  <span>{userProfile?.displayName || userProfile?.name}</span>
                  {userProfile?.isIdVerified && (
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
                  )}
                </div>
                <div className="text-[10px] text-slate-400 font-mono truncate">{userProfile?.city}</div>
                <div className="text-[10px] font-bold text-purple-300 font-mono">₹{userProfile?.hourlyRate || 1500}/hr</div>
              </div>
            </div>
            {/* Companion Profile Status Badge Notification */}
            <div className="pt-1.5 border-t border-slate-800/80 flex items-center justify-between">
              <ProfileStatusBadge
                userProfile={userProfile}
                size="xs"
                variant="compact"
                onNavigateToVerification={() => setActiveSection('verification')}
              />
            </div>
          </div>

          {/* SRD Navigation Items */}
          <nav className="space-y-1 text-xs font-bold">
            {[
              { id: 'dashboard', label: 'Dashboard', icon: Sparkles },
              {
                id: 'document_status',
                label: 'Document Status',
                icon: FileCheck,
                alert: userProfile?.profileStatus === 'documents_incomplete',
              },
              { id: 'requests', label: 'Pairing Requests', icon: Heart, badge: pairings.filter(p => p.pair_status === 'AWAITING_PAYMENTS').length },
              { id: 'messages', label: 'Messages / Chat', icon: MessageSquare },
              { id: 'matches', label: 'Matches', icon: Users },
              { id: 'profile', label: 'Profile Management', icon: User },
              { id: 'lifecycle', label: 'Profile Lifecycle & Status', icon: ShieldAlert },
              { id: 'verification', label: 'Verification Center', icon: ShieldCheck, alert: verificationStatus !== 'ACTIVE' },
              { id: 'availability', label: 'Availability', icon: Calendar },
              { id: 'payments', label: 'Payments & Earnings', icon: DollarSign },
              { id: 'notifications', label: 'Notifications', icon: Bell },
              { id: 'settings', label: 'Settings', icon: Settings },
              { id: 'support', label: 'Safety & Support', icon: HelpCircle },
            ].map((item) => {
              const Icon = item.icon;
              const isActive = activeSection === item.id;
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => setActiveSection(item.id as any)}
                  className={`w-full flex items-center justify-between px-3 py-2.5 rounded-xl transition-all cursor-pointer ${
                    isActive
                      ? 'bg-purple-600 text-white shadow-lg shadow-purple-950/40'
                      : 'text-slate-400 hover:text-white hover:bg-slate-800'
                  }`}
                >
                  <div className="flex items-center gap-2.5">
                    <Icon className="w-4 h-4 shrink-0" />
                    <span>{item.label}</span>
                  </div>
                  {item.badge !== undefined && item.badge > 0 && (
                    <span className="w-5 h-5 rounded-full bg-rose-500 text-white font-mono text-[10px] flex items-center justify-center font-bold">
                      {item.badge}
                    </span>
                  )}
                  {item.alert && (
                    <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
                  )}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Bottom Logout & Back to Site */}
        <div className="p-4 border-t border-slate-800 space-y-2">
          <Link
            to="/companions"
            className="w-full py-2 bg-slate-950 hover:bg-slate-800 text-slate-300 rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-1.5"
          >
            <Eye className="w-3.5 h-3.5" />
            <span>Public Discover</span>
          </Link>
          <button
            type="button"
            onClick={() => {
              logout();
              navigate('/login');
            }}
            className="w-full py-2 bg-rose-950/60 hover:bg-rose-900 border border-rose-900/60 text-rose-300 rounded-xl text-xs font-bold transition-all cursor-pointer flex items-center justify-center gap-1.5"
          >
            <LogOut className="w-3.5 h-3.5" />
            <span>Sign Out</span>
          </button>
        </div>
      </aside>

      {/* ========================================================= */}
      {/* MAIN CONTENT AREA */}
      {/* ========================================================= */}
      <main className="flex-1 p-4 sm:p-6 lg:p-8 overflow-y-auto space-y-6">
        {/* Toast Action Notice */}
        {actionNotice && (
          <div
            className={`p-3.5 rounded-2xl text-xs flex items-center gap-2 animate-in fade-in ${
              actionNotice.type === 'success'
                ? 'bg-emerald-950 border border-emerald-800 text-emerald-300'
                : actionNotice.type === 'error'
                ? 'bg-rose-950 border border-rose-800 text-rose-300'
                : 'bg-blue-950 border border-blue-800 text-blue-300'
            }`}
          >
            <CheckCircle2 className="w-4 h-4 shrink-0" />
            <span>{actionNotice.message}</span>
          </div>
        )}

        {/* Profile Status Notification Banner (Alerts companion of incomplete document statuses) */}
        <ProfileStatusBadge
          userProfile={userProfile}
          variant="notification"
          onNavigateToVerification={() => setActiveSection('verification')}
        />

        {/* SECTION 1: DASHBOARD OVERVIEW */}
        {activeSection === 'dashboard' && (
          <div className="space-y-6 animate-in fade-in duration-150">
            {/* Header Banner */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <h1 className="text-2xl font-black text-white tracking-tight">
                  Welcome, {userProfile?.displayName || userProfile?.name}!
                </h1>
                <p className="text-xs text-slate-400 mt-1">
                  Manage your pairing requests, view client messages, and track dual-payment verified bookings.
                </p>
              </div>

              {/* Status Indicator & Badges */}
              <div className="flex flex-wrap items-center gap-2">
                {/* Section 25: Companion Active/Inactive Toggle Button */}
                <button
                  type="button"
                  id="companion-active-inactive-toggle"
                  onClick={handleToggleAvailability}
                  disabled={isTogglingAvailability}
                  className={`px-3.5 py-1.5 rounded-full text-xs font-black font-mono border flex items-center gap-2 transition-all cursor-pointer shadow-md active:scale-95 ${
                    isCompanionAvailable
                      ? 'bg-emerald-950/90 hover:bg-emerald-900 border-emerald-500 text-emerald-300 ring-2 ring-emerald-500/30'
                      : 'bg-slate-900 hover:bg-slate-800 border-slate-700 text-slate-400'
                  }`}
                  title={
                    isCompanionAvailable
                      ? 'Currently ACTIVE for bookings — Click to switch to INACTIVE'
                      : 'Currently INACTIVE — Click to switch to ACTIVE'
                  }
                >
                  <span
                    className={`w-2.5 h-2.5 rounded-full ${
                      isCompanionAvailable
                        ? 'bg-emerald-400 animate-pulse'
                        : 'bg-slate-500'
                    }`}
                  />
                  <span>
                    STATUS: {isCompanionAvailable ? 'ACTIVE' : 'INACTIVE'}
                  </span>
                  <span className="text-[10px] opacity-75 font-normal hidden sm:inline">
                    ({isCompanionAvailable ? 'Accepting Bookings' : 'Paused'})
                  </span>
                </button>

                <ProfileStatusBadge
                  userProfile={userProfile}
                  onNavigateToVerification={() => setActiveSection('verification')}
                />

                <CompanionProfileStatusIndicator
                  userProfile={userProfile}
                  onNavigateToVerification={() => setActiveSection('verification')}
                  compact
                />

                <button
                  type="button"
                  onClick={() => setActiveSection('document_status')}
                  className="px-3 py-1 rounded-full text-xs font-bold font-mono border border-blue-800 bg-blue-950/60 hover:bg-blue-900 text-blue-300 flex items-center gap-1.5 transition-all cursor-pointer"
                  title="View Required Documents & Upload Status"
                >
                  <FileText className="w-3 h-3 text-blue-400" />
                  <span>DOCUMENT STATUS</span>
                  <ChevronRight className="w-3 h-3 text-blue-400" />
                </button>

                <button
                  type="button"
                  onClick={() => setActiveSection('lifecycle')}
                  className="px-3 py-1 rounded-full text-xs font-bold font-mono border border-purple-800 bg-purple-950/60 hover:bg-purple-900 text-purple-300 flex items-center gap-1.5 transition-all cursor-pointer"
                  title="Manage Profile Lifecycle & Availability"
                >
                  <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                  <span>LIFECYCLE STATUS</span>
                  <ChevronRight className="w-3 h-3 text-purple-400" />
                </button>
              </div>
            </div>

            {/* Visual Status Indicator: displays current profileStatus and lists specific missing documents */}
            <CompanionProfileStatusIndicator
              userProfile={userProfile}
              onNavigateToVerification={() => setActiveSection('document_status')}
            />

            {/* New Document Status Section: Centralized validation utility visually listing required documents and upload status */}
            <DocumentStatusSection
              userProfile={userProfile}
              companionId={(userProfile as any)?.uid || (userProfile?.email ? userProfile.email.replace(/[^a-zA-Z0-9]/g, '_') : 'CP1024')}
              onNavigateToVerification={() => setActiveSection('verification')}
            />

            {/* Definitive Mandatory Payment Trigger Rule Card */}
            <div className="p-4 rounded-3xl bg-gradient-to-r from-purple-950/70 via-slate-900 to-indigo-950/70 border border-purple-800/60 space-y-2">
              <div className="flex items-center gap-2 text-purple-300 font-bold text-xs">
                <ShieldCheck className="w-4 h-4 text-purple-400" />
                <span>Mandatory Dual-Payment Trigger Rule:</span>
              </div>
              <p className="text-xs text-slate-300 leading-relaxed">
                When a client requests a pairing, the booking remains in <strong>AWAITING_PAYMENTS</strong> until both parties complete server-verified payments:
                <br />
                <code className="text-purple-300 font-bold">User Payment = SUCCESS AND Companion Payment = SUCCESS → PAIR ACTIVE</code>.
              </p>
            </div>

            {/* Section 36: Companion V2 Realtime Metrics Banner */}
            <div className="p-6 rounded-3xl bg-slate-900 border border-slate-800 shadow-xl space-y-4">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-slate-800 pb-3">
                <div className="flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full bg-emerald-400 animate-pulse" />
                  <span className="text-xs font-mono font-bold uppercase tracking-wider text-purple-300">
                    Live Operations & Escrow Payouts Dashboard
                  </span>
                </div>
                <span className="text-[11px] font-mono text-slate-400">
                  85% Companion Share • 15% Safety Guarantee
                </span>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                <div className="p-3.5 rounded-2xl bg-slate-950 border border-slate-800/80">
                  <span className="text-[10px] font-mono uppercase text-slate-400 block font-bold">Today's Bookings</span>
                  <span className="text-2xl font-black text-white font-mono mt-1 block">
                    {v2Bookings.filter(b => b.booking_date === new Date().toISOString().substring(0, 10)).length}
                  </span>
                  <span className="text-[10px] text-purple-400 font-mono">Scheduled</span>
                </div>

                <div className="p-3.5 rounded-2xl bg-slate-950 border border-slate-800/80">
                  <span className="text-[10px] font-mono uppercase text-slate-400 block font-bold">Upcoming</span>
                  <span className="text-2xl font-black text-blue-400 font-mono mt-1 block">
                    {v2Bookings.filter(b => ['COMPANION_ACCEPTED', 'UPCOMING', 'IN_PROGRESS'].includes(b.status)).length}
                  </span>
                  <span className="text-[10px] text-blue-400/80 font-mono">Confirmed</span>
                </div>

                <div className="p-3.5 rounded-2xl bg-slate-950 border border-slate-800/80">
                  <span className="text-[10px] font-mono uppercase text-slate-400 block font-bold">Pending Requests</span>
                  <span className="text-2xl font-black text-amber-400 font-mono mt-1 block">
                    {v2Bookings.filter(b => b.status === 'CONFIRMED').length}
                  </span>
                  <span className="text-[10px] text-amber-400/80 font-mono">Needs Accept</span>
                </div>

                <div className="p-3.5 rounded-2xl bg-slate-950 border border-slate-800/80">
                  <span className="text-[10px] font-mono uppercase text-slate-400 block font-bold">Total Earnings</span>
                  <span className="text-2xl font-black text-emerald-400 font-mono mt-1 block">
                    ₹{payoutStats.totalEarned.toLocaleString('en-IN')}
                  </span>
                  <span className="text-[10px] text-emerald-500 font-mono">85% Net Share</span>
                </div>

                <div className="p-3.5 rounded-2xl bg-slate-950 border border-slate-800/80 col-span-2 sm:col-span-1">
                  <span className="text-[10px] font-mono uppercase text-slate-400 block font-bold">Pending Payout</span>
                  <span className="text-2xl font-black text-purple-300 font-mono mt-1 block">
                    ₹{payoutStats.pendingBalance.toLocaleString('en-IN')}
                  </span>
                  <span className="text-[10px] text-purple-400/80 font-mono">In Settlement</span>
                </div>
              </div>
            </div>

            {/* V2 Section 4: Pending Booking Requests (Section 32 & 4 from Spec) */}
            <div className="p-6 rounded-3xl bg-slate-900 border border-slate-800 shadow-xl space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="inline-flex items-center gap-1.5 text-amber-400 text-xs font-bold font-mono">
                    <Bell className="w-3.5 h-3.5 animate-bounce" />
                    <span>PENDING BOOKING REQUESTS</span>
                  </div>
                  <h3 className="text-lg font-black text-white mt-0.5">New Client Booking Requests</h3>
                  <p className="text-xs text-slate-400">
                    Clients who have completed payment and are waiting for your acceptance.
                  </p>
                </div>
                <span className="px-2.5 py-1 rounded-full bg-amber-950 border border-amber-800 text-amber-300 text-xs font-mono font-bold">
                  {v2Bookings.filter(b => b.status === 'CONFIRMED').length} Pending
                </span>
              </div>

              {v2Bookings.filter(b => b.status === 'CONFIRMED').length === 0 ? (
                <div className="p-6 rounded-2xl bg-slate-950/70 border border-slate-800 text-center text-xs text-slate-400">
                  No pending booking requests right now. New booking notifications will appear here instantly.
                </div>
              ) : (
                <div className="space-y-3">
                  {v2Bookings.filter(b => b.status === 'CONFIRMED').map((b) => (
                    <div
                      key={b.id}
                      className="p-5 rounded-2xl bg-slate-950 border border-amber-900/60 shadow-lg flex flex-col md:flex-row items-start md:items-center justify-between gap-4"
                    >
                      <div className="space-y-1.5">
                        <div className="flex items-center gap-2">
                          <span className="text-base font-black text-white">{b.client_name}</span>
                          <span className="px-2 py-0.5 rounded-full bg-amber-950 border border-amber-800 text-amber-300 font-mono text-[10px] font-bold">
                            #{b.id}
                          </span>
                        </div>
                        <div className="flex flex-wrap items-center gap-3 text-xs text-slate-300">
                          <span className="font-mono text-purple-300">📅 {b.booking_date}</span>
                          <span className="font-mono text-purple-300">🕕 {b.start_time} ({b.duration_hours}h)</span>
                          <span className="text-slate-300">📍 {b.location_name}</span>
                          <a
                            href={b.maps_url || `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(b.location_name)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-[11px] font-bold text-pink-400 hover:text-pink-300 underline"
                          >
                            <span>Open in Google Maps</span>
                            <ExternalLink className="w-3 h-3" />
                          </a>
                        </div>
                        {b.location_note && (
                          <p className="text-[11px] text-slate-400 italic">"{b.location_note}"</p>
                        )}
                        <div className="font-mono text-[11px] text-slate-400 pt-1">
                          Gross Fee: <strong className="text-slate-200">₹{b.gross_amount}</strong> • Your Net 85% Payout: <strong className="text-emerald-400">₹{b.companion_amount}</strong>
                        </div>
                      </div>

                      <div className="flex items-center gap-2 shrink-0 w-full md:w-auto">
                        <button
                          type="button"
                          onClick={() => handleAcceptBooking(b.id)}
                          className="flex-1 md:flex-initial px-5 py-2.5 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white font-bold text-xs rounded-xl shadow-lg shadow-emerald-950 transition-all cursor-pointer flex items-center justify-center gap-1.5"
                        >
                          <CheckCircle2 className="w-4 h-4" />
                          <span>Accept Booking</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDeclineBooking(b.id)}
                          className="flex-1 md:flex-initial px-4 py-2.5 bg-slate-800 hover:bg-slate-700 text-slate-300 font-bold text-xs rounded-xl transition-all cursor-pointer"
                        >
                          Decline
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* V2 Section: Confirmed Upcoming Bookings & Meeting Day Controls */}
            <div className="p-6 rounded-3xl bg-slate-900 border border-slate-800 shadow-xl space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="inline-flex items-center gap-1.5 text-blue-400 text-xs font-bold font-mono">
                    <Calendar className="w-3.5 h-3.5" />
                    <span>CONFIRMED UPCOMING MEETINGS</span>
                  </div>
                  <h3 className="text-lg font-black text-white mt-0.5">Active & In-Progress Bookings</h3>
                  <p className="text-xs text-slate-400">
                    Use Google Maps for route navigation, chat privately with client, and complete meetup via 6-digit OTP.
                  </p>
                </div>
              </div>

              {v2Bookings.filter(b => ['COMPANION_ACCEPTED', 'UPCOMING', 'IN_PROGRESS'].includes(b.status)).length === 0 ? (
                <div className="p-6 rounded-2xl bg-slate-950/70 border border-slate-800 text-center text-xs text-slate-400">
                  No upcoming meetings confirmed currently.
                </div>
              ) : (
                <div className="space-y-3">
                  {v2Bookings.filter(b => ['COMPANION_ACCEPTED', 'UPCOMING', 'IN_PROGRESS'].includes(b.status)).map((b) => (
                    <div
                      key={b.id}
                      className="p-5 rounded-2xl bg-slate-950 border border-slate-800 shadow-lg flex flex-col md:flex-row items-start md:items-center justify-between gap-4"
                    >
                      <div className="space-y-1.5">
                        <div className="flex items-center gap-2">
                          <span className="text-base font-black text-white">{b.client_name}</span>
                          <span className="px-2 py-0.5 rounded-full bg-blue-950 border border-blue-800 text-blue-300 font-mono text-[10px] font-bold">
                            #{b.id}
                          </span>
                          <span className="px-2 py-0.5 rounded-full bg-purple-950 text-purple-300 font-mono text-[10px]">
                            {b.status === 'IN_PROGRESS' ? '● In Progress' : 'Upcoming'}
                          </span>
                        </div>
                        <div className="flex flex-wrap items-center gap-3 text-xs text-slate-300 font-mono">
                          <span>📅 {b.booking_date}</span>
                          <span>🕕 {b.start_time} - {b.end_time}</span>
                          <span className="text-slate-300">📍 {b.location_name}</span>
                        </div>
                        <div className="text-[11px] font-mono text-slate-400">
                          Net Companion Payout: <strong className="text-emerald-400">₹{b.companion_amount}</strong> (85% after 15% platform safety fee)
                        </div>
                      </div>

                      <div className="flex flex-wrap items-center gap-2 shrink-0 w-full md:w-auto">
                        {/* One-Click Google Maps Link (Rule 10) */}
                        <a
                          href={b.maps_url || `https://www.google.com/maps/dir/?api=1&destination=${b.latitude || 22.62},${b.longitude || 88.46}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="px-3 py-2 rounded-xl bg-emerald-950/80 hover:bg-emerald-900 border border-emerald-700 text-emerald-300 text-xs font-bold transition-all flex items-center gap-1.5"
                        >
                          <MapPin className="w-3.5 h-3.5 text-emerald-400" />
                          <span>Open in Google Maps</span>
                          <ExternalLink className="w-3 h-3 opacity-60" />
                        </a>

                        {/* Chat Button (Rule 7) */}
                        <button
                          type="button"
                          onClick={() => setActiveChatBooking(b)}
                          className="px-3 py-2 rounded-xl bg-purple-600 hover:bg-purple-500 text-white font-bold text-xs transition-all flex items-center gap-1.5 cursor-pointer shadow-md shadow-purple-950"
                        >
                          <MessageSquare className="w-3.5 h-3.5" />
                          <span>Chat Now</span>
                        </button>

                        {/* Check-In Button (Rule 12 & 13) */}
                        {!b.companion_checked_in && (
                          <button
                            type="button"
                            onClick={() => handleCheckInBooking(b.id)}
                            className="px-3 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-200 font-bold text-xs transition-all cursor-pointer"
                          >
                            I'm At Location
                          </button>
                        )}

                        {/* Let's Start Meeting Button (Generates Meeting Start OTP via APITXT -> Enter Your Meeting Start OTP) */}
                        {b.status !== 'IN_PROGRESS' ? (
                          <button
                            type="button"
                            onClick={() => void handleOpenStartMeetingModal(b)}
                            className="px-4 py-2 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white font-bold text-xs transition-all cursor-pointer flex items-center gap-1.5 shadow-md shadow-emerald-950"
                          >
                            <Clock className="w-3.5 h-3.5" />
                            <span>Arrived — Let&apos;s Start Meeting</span>
                          </button>
                        ) : (
                          <div className="px-3 py-1.5 rounded-xl bg-emerald-950/90 border border-emerald-700 text-emerald-300 font-mono text-xs font-bold flex items-center gap-1.5">
                            <Clock className="w-3.5 h-3.5 text-emerald-400 animate-pulse" />
                            <span>Countdown: {getBookingRemainingCountdown(b) || '00:00:00'}</span>
                          </div>
                        )}

                        {/* Slide-to-Complete Meeting Button (Rule 14 & 16: Slide to Complete -> Enter Client OTP) */}
                        <button
                          type="button"
                          onClick={() => {
                            setActiveOtpBooking(b);
                            setSlideToCompleteProgress(0);
                            setSlideToCompleteConfirmed(false);
                            setCompletionOtpInput('');
                            setOtpVerifyError('');
                            setOtpVerifySuccess(null);
                          }}
                          className="px-4 py-2 rounded-xl bg-gradient-to-r from-pink-600 to-rose-600 hover:from-pink-500 hover:to-rose-500 text-white font-bold text-xs transition-all cursor-pointer flex items-center gap-1.5 shadow-md shadow-pink-950"
                        >
                          <KeyRound className="w-3.5 h-3.5" />
                          <span>Complete Meeting</span>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Active Pairings Quick View */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-black text-white uppercase tracking-wider font-mono">
                  Recent Pairing Requests & Dual-Payment Status
                </h3>
                <button
                  type="button"
                  onClick={() => setActiveSection('requests')}
                  className="text-xs text-purple-400 hover:text-purple-300 font-bold cursor-pointer"
                >
                  View All ({pairings.length})
                </button>
              </div>

              <div className="space-y-3">
                {pairings.length === 0 ? (
                  <div className="p-8 text-center bg-slate-900 border border-slate-800 rounded-3xl text-slate-400 text-xs">
                    No pairing requests currently. Once clients discover your profile and book a meetup, they will appear here!
                  </div>
                ) : (
                  pairings.slice(0, 3).map((pair) => (
                    <div
                      key={pair.id}
                      className="p-4 rounded-3xl bg-slate-900 border border-slate-800 shadow-xl flex flex-col md:flex-row items-start md:items-center justify-between gap-4"
                    >
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <span className="font-black text-white text-sm">{pair.service_type}</span>
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-800 text-purple-300 font-mono font-bold">
                            {pair.id}
                          </span>
                        </div>
                        <p className="text-xs text-slate-400">
                          Client: <strong className="text-slate-200">{pair.user_name}</strong> • Date: {pair.scheduled_date} ({pair.hours} hrs)
                        </p>
                        <div className="flex items-center gap-2 text-[11px] pt-1 font-mono">
                          <span
                            className={`px-2 py-0.5 rounded-full ${
                              pair.user_payment_status === 'SUCCESS'
                                ? 'bg-emerald-950 text-emerald-400'
                                : 'bg-amber-950 text-amber-400'
                            }`}
                          >
                            User: {pair.user_payment_status}
                          </span>
                          <span
                            className={`px-2 py-0.5 rounded-full ${
                              pair.companion_payment_status === 'SUCCESS'
                                ? 'bg-emerald-950 text-emerald-400'
                                : 'bg-amber-950 text-amber-400'
                            }`}
                          >
                            Companion: {pair.companion_payment_status}
                          </span>
                          <span
                            className={`px-2 py-0.5 rounded-full font-bold ${
                              pair.pair_status === 'ACTIVE'
                                ? 'bg-emerald-600 text-white'
                                : 'bg-slate-800 text-slate-300'
                            }`}
                          >
                            {pair.pair_status}
                          </span>
                        </div>
                      </div>

                      {/* Payment Action if companion has not paid */}
                      {pair.companion_payment_status !== 'SUCCESS' && (
                        <button
                          type="button"
                          onClick={() => handlePayCompanionSide(pair.id)}
                          className="px-4 py-2 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white rounded-xl text-xs font-bold transition-all shadow-lg shadow-purple-950/40 cursor-pointer shrink-0"
                        >
                          Confirm & Pay ₹{pair.companion_fee}
                        </button>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        )}

        {/* SECTION 2: PROFILE MANAGEMENT */}
        {activeSection === 'profile' && (
          <div className="space-y-6 animate-in fade-in duration-150">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-black text-white tracking-tight">Profile Management</h1>
                <p className="text-xs text-slate-400">Update your public presentation, hourly rate, and bio.</p>
              </div>
              <button
                type="button"
                onClick={() => setIsEditingProfile(!isEditingProfile)}
                className="px-3.5 py-1.5 bg-purple-600 hover:bg-purple-500 text-white rounded-xl text-xs font-bold transition-all cursor-pointer flex items-center gap-1.5"
              >
                <Edit3 className="w-3.5 h-3.5" />
                <span>{isEditingProfile ? 'Cancel' : 'Edit Profile'}</span>
              </button>
            </div>

            <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 space-y-4 shadow-xl">
              <div className="flex items-center gap-4">
                <img
                  src={userProfile?.avatarUrl || 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&w=300&q=80'}
                  alt={userProfile?.name}
                  className="w-16 h-16 rounded-2xl object-cover border-2 border-purple-500"
                />
                <div>
                  <div className="flex items-center gap-2.5 flex-wrap">
                    <h3 className="font-black text-white text-base">{userProfile?.displayName || userProfile?.name}</h3>
                    <ProfileStatusBadge
                      userProfile={userProfile}
                      size="sm"
                      onNavigateToVerification={() => setActiveSection('verification')}
                    />
                  </div>
                  <p className="text-xs text-slate-400">{userProfile?.email} • {userProfile?.city}</p>
                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                    <span className="text-xs text-purple-400 font-bold font-mono">₹{userProfile?.hourlyRate || 1500}/hr</span>
                    <span className="text-xs text-indigo-400 font-bold font-mono">1/3 Day: ₹{userProfile?.rateOneThirdDay || 2000}</span>
                    <span className="text-xs text-sky-400 font-bold font-mono">Half-Day: ₹{userProfile?.rateHalfDay || 3000}</span>
                    <span className="text-xs text-emerald-400 font-bold font-mono">Full-Day: ₹{userProfile?.rateFullDay || 5000}</span>
                  </div>
                </div>
              </div>

              {/* 4-TIER RATES OVERVIEW CARDS */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 pt-2">
                <div className="p-3 bg-purple-950/30 border border-purple-800/40 rounded-2xl text-left space-y-1">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-purple-300 block">1. Hourly Rate</span>
                  <div className="text-base font-black text-white font-mono">₹{userProfile?.hourlyRate || 1500}<span className="text-[10px] text-slate-400 font-normal">/hr</span></div>
                  <div className="text-[10px] text-emerald-400 font-mono">You get 85%: ₹{Math.round((userProfile?.hourlyRate || 1500) * 0.85)}</div>
                  <div className="text-[9px] text-slate-400">Min ₹600/hr</div>
                </div>

                <div className="p-3 bg-indigo-950/30 border border-indigo-800/40 rounded-2xl text-left space-y-1">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-indigo-300 block">2. 1/3 Day (6h)</span>
                  <div className="text-base font-black text-white font-mono">₹{userProfile?.rateOneThirdDay || 2000}</div>
                  <div className="text-[10px] text-emerald-400 font-mono">You get 85%: ₹{Math.round((userProfile?.rateOneThirdDay || 2000) * 0.85)}</div>
                  <div className="text-[9px] text-slate-400">Up to 6 hours</div>
                </div>

                <div className="p-3 bg-sky-950/30 border border-sky-800/40 rounded-2xl text-left space-y-1">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-sky-300 block">3. Half-Day (12h)</span>
                  <div className="text-base font-black text-white font-mono">₹{userProfile?.rateHalfDay || 3000}</div>
                  <div className="text-[10px] text-emerald-400 font-mono">You get 85%: ₹{Math.round((userProfile?.rateHalfDay || 3000) * 0.85)}</div>
                  <div className="text-[9px] text-slate-400">6–12 hours</div>
                </div>

                <div className="p-3 bg-emerald-950/30 border border-emerald-800/40 rounded-2xl text-left space-y-1">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-300 block">4. Full-Day (24h)</span>
                  <div className="text-base font-black text-white font-mono">₹{userProfile?.rateFullDay || 5000}</div>
                  <div className="text-[10px] text-emerald-400 font-mono">You get 85%: ₹{Math.round((userProfile?.rateFullDay || 5000) * 0.85)}</div>
                  <div className="text-[9px] text-slate-400">12–24 hours</div>
                </div>
              </div>

              {isEditingProfile ? (
                <div className="space-y-4 pt-4 border-t border-slate-800 text-xs">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <label className="font-bold text-slate-300">Display Name</label>
                      <input
                        type="text"
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        className="w-full px-3 py-2 bg-slate-950 border border-slate-800 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="font-bold text-slate-300">Primary City Location</label>
                      <input
                        type="text"
                        value={editCity}
                        onChange={(e) => setEditCity(e.target.value)}
                        className="w-full px-3 py-2 bg-slate-950 border border-slate-800 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                      />
                    </div>
                  </div>

                  {/* CUSTOM RATES CONFIGURATION */}
                  <div className="p-4 bg-slate-950/80 rounded-2xl border border-slate-800 space-y-3">
                    <div className="flex items-center justify-between">
                      <h4 className="font-bold text-white flex items-center gap-1.5 text-xs">
                        <IndianRupee className="w-4 h-4 text-purple-400" />
                        <span>Booking Duration & Pricing Rates (You receive 85% • Platform 15%)</span>
                      </h4>
                      <span className="text-[10px] text-purple-400 font-mono">Min Hourly: ₹600</span>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
                      <div className="space-y-1">
                        <label className="font-bold text-slate-300">1. Hourly Rate (₹)</label>
                        <input
                          type="number"
                          min={600}
                          value={editRate}
                          onChange={(e) => setEditRate(Number(e.target.value))}
                          className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-purple-500 font-mono font-bold"
                        />
                        <span className="text-[10px] text-emerald-400 block font-mono">Net (80%): ₹{Math.round(editRate * 0.8)}</span>
                      </div>

                      <div className="space-y-1">
                        <label className="font-bold text-slate-300">2. 1/3 Day Rate (6h ₹)</label>
                        <input
                          type="number"
                          value={editRateOneThirdDay}
                          onChange={(e) => setEditRateOneThirdDay(Number(e.target.value))}
                          className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 font-mono font-bold"
                        />
                        <span className="text-[10px] text-emerald-400 block font-mono">Net (80%): ₹{Math.round(editRateOneThirdDay * 0.8)}</span>
                      </div>

                      <div className="space-y-1">
                        <label className="font-bold text-slate-300">3. Half-Day Rate (12h ₹)</label>
                        <input
                          type="number"
                          value={editRateHalfDay}
                          onChange={(e) => setEditRateHalfDay(Number(e.target.value))}
                          className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-sky-500 font-mono font-bold"
                        />
                        <span className="text-[10px] text-emerald-400 block font-mono">Net (80%): ₹{Math.round(editRateHalfDay * 0.8)}</span>
                      </div>

                      <div className="space-y-1">
                        <label className="font-bold text-slate-300">4. Full-Day Rate (24h ₹)</label>
                        <input
                          type="number"
                          value={editRateFullDay}
                          onChange={(e) => setEditRateFullDay(Number(e.target.value))}
                          className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-emerald-500 font-mono font-bold"
                        />
                        <span className="text-[10px] text-emerald-400 block font-mono">Net (80%): ₹{Math.round(editRateFullDay * 0.8)}</span>
                      </div>
                    </div>
                  </div>

                  {/* SERVICE CATEGORIES WITH TOURIST GUIDE & DRIVER */}
                  <div className="p-4 bg-slate-950/80 rounded-2xl border border-slate-800 space-y-2">
                    <label className="font-bold text-slate-300 block">
                      Offered Service Categories (Select all that apply)
                    </label>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                      {[
                        { name: 'Dinner', icon: Sparkles },
                        { name: 'Movies', icon: Sparkles },
                        { name: 'Tourist Guide', icon: Compass, badge: 'New', color: 'text-emerald-400' },
                        { name: 'Driver', icon: Car, badge: 'Licence Req.', color: 'text-sky-400' },
                        { name: 'Shopping', icon: Sparkles },
                        { name: 'Travel', icon: Sparkles },
                        { name: 'Parties', icon: Sparkles },
                        { name: 'Business Events', icon: Sparkles },
                      ].map((srv) => {
                        const isSelected = selectedServices.includes(srv.name);
                        const Icon = srv.icon;
                        return (
                          <button
                            key={srv.name}
                            type="button"
                            onClick={() => handleToggleService(srv.name)}
                            className={`p-2.5 rounded-xl border text-left flex items-center justify-between transition-all cursor-pointer ${
                              isSelected
                                ? 'bg-purple-900/40 border-purple-500 text-white'
                                : 'bg-slate-900 border-slate-800 text-slate-400 hover:border-slate-700'
                            }`}
                          >
                            <div className="flex items-center gap-1.5 truncate">
                              <Icon className={`w-3.5 h-3.5 ${srv.color || 'text-purple-400'}`} />
                              <span className="font-bold truncate text-[11px]">{srv.name}</span>
                            </div>
                            {srv.badge && (
                              <span className="text-[9px] px-1 py-0.5 rounded bg-purple-500/20 text-purple-300 font-bold shrink-0">
                                {srv.badge}
                              </span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* MANDATORY DRIVING LICENCE SECTION FOR DRIVER */}
                  {selectedServices.includes('Driver') && (
                    <div className="p-4 bg-sky-950/30 border-2 border-sky-500/40 rounded-2xl space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Car className="w-5 h-5 text-sky-400" />
                          <div>
                            <h4 className="font-bold text-white text-xs">Mandatory Driving Licence Verification</h4>
                            <p className="text-[10px] text-slate-400">
                              Driver service requires a valid Driving Licence and remains inactive until Admin approval.
                            </p>
                          </div>
                        </div>
                        <span
                          className={`text-[10px] px-2.5 py-1 rounded-full font-bold font-mono ${
                            drivingLicenceStatus === 'APPROVED'
                              ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                              : drivingLicenceStatus === 'SUBMITTED'
                              ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30'
                              : drivingLicenceStatus === 'REJECTED'
                              ? 'bg-rose-500/20 text-rose-400 border border-rose-500/30'
                              : 'bg-purple-500/20 text-purple-400 border border-purple-500/30'
                          }`}
                        >
                          Status: {drivingLicenceStatus}
                        </span>
                      </div>

                      {drivingLicenceStatus === 'REJECTED' && dlRejectionReason && (
                        <div className="p-2.5 bg-rose-950/40 border border-rose-500/30 rounded-xl text-[11px] text-rose-300 flex items-start gap-1.5">
                          <AlertCircle className="w-4 h-4 shrink-0 text-rose-400 mt-0.5" />
                          <div>
                            <strong>Admin Rejection Note:</strong> {dlRejectionReason}
                            <p className="text-[10px] text-slate-400 mt-0.5">Please upload a valid, clear replacement image below to re-submit.</p>
                          </div>
                        </div>
                      )}

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <label className="font-bold text-slate-300">Driving Licence Number *</label>
                          <input
                            type="text"
                            value={dlNumber}
                            onChange={(e) => setDlNumber(e.target.value)}
                            placeholder="e.g. WB-01-2024009876"
                            className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-white font-mono"
                          />
                        </div>

                        <div className="space-y-1">
                          <label className="font-bold text-slate-300">Licence Expiry Date *</label>
                          <input
                            type="date"
                            value={dlExpiry}
                            onChange={(e) => setDlExpiry(e.target.value)}
                            className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-white font-mono"
                          />
                        </div>

                        <div className="space-y-1">
                          <label className="font-bold text-slate-300">Front Side Image URL *</label>
                          <input
                            type="text"
                            value={dlFront}
                            onChange={(e) => setDlFront(e.target.value)}
                            className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-white font-mono text-[11px]"
                          />
                        </div>

                        <div className="space-y-1">
                          <label className="font-bold text-slate-300">Back Side Image URL *</label>
                          <input
                            type="text"
                            value={dlBack}
                            onChange={(e) => setDlBack(e.target.value)}
                            className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-white font-mono text-[11px]"
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="space-y-1">
                    <label className="font-bold text-slate-300">Bio & Presentation</label>
                    <textarea
                      rows={3}
                      value={editBio}
                      onChange={(e) => setEditBio(e.target.value)}
                      className="w-full px-3 py-2 bg-slate-950 border border-slate-800 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                  </div>

                  <div className="pt-2 flex justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => setIsEditingProfile(false)}
                      className="px-4 py-2 bg-slate-800 text-slate-300 rounded-xl text-xs font-bold cursor-pointer"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleSaveProfile}
                      className="px-5 py-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-xl text-xs font-bold transition-all cursor-pointer flex items-center gap-1.5"
                    >
                      <Save className="w-3.5 h-3.5" />
                      <span>Save Profile Changes</span>
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-3 pt-3 border-t border-slate-800 text-xs text-slate-300">
                  <p><strong>About:</strong> {userProfile?.bio || 'Professional dining & social companion.'}</p>
                  <div className="flex items-center gap-2 flex-wrap">
                    <strong className="text-white">Active Services:</strong>
                    {selectedServices.map((srv) => (
                      <span
                        key={srv}
                        className={`px-2 py-0.5 rounded-lg text-[10px] font-bold ${
                          srv === 'Tourist Guide'
                            ? 'bg-emerald-950 text-emerald-300 border border-emerald-700'
                            : srv === 'Driver'
                            ? 'bg-sky-950 text-sky-300 border border-sky-700'
                            : 'bg-slate-800 text-slate-300'
                        }`}
                      >
                        {srv}
                      </span>
                    ))}
                  </div>

                  {selectedServices.includes('Driver') && (
                    <div className="p-3 bg-slate-950 rounded-xl border border-slate-800 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Car className="w-4 h-4 text-sky-400" />
                        <div>
                          <strong className="text-white block text-xs">Driver Service Verification:</strong>
                          <span className="text-[11px] text-slate-400">
                            Licence: {dlNumber || 'On File'} • Exp: {dlExpiry}
                          </span>
                        </div>
                      </div>
                      <span
                        className={`text-[10px] px-2.5 py-0.5 rounded-full font-bold font-mono ${
                          drivingLicenceStatus === 'APPROVED'
                            ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                            : 'bg-amber-500/20 text-amber-400 border border-amber-500/30'
                        }`}
                      >
                        {drivingLicenceStatus === 'APPROVED' ? 'Approved & Active ✓' : 'Inactive (Review Pending)'}
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* NEW SECTION: DOCUMENT STATUS (CENTRALIZED VALIDATION UTILITY) */}
        {activeSection === 'document_status' && (
          <div className="space-y-6 animate-in fade-in duration-150">
            <DocumentStatusSection
              userProfile={userProfile}
              companionId={(userProfile as any)?.uid || (userProfile?.email ? userProfile.email.replace(/[^a-zA-Z0-9]/g, '_') : 'CP1024')}
              onNavigateToVerification={() => setActiveSection('verification')}
            />
          </div>
        )}

        {/* SECTION 3: COMPLETE VERIFICATION CENTER (PHASE 1 IMPLEMENTATION) */}
        {activeSection === 'verification' && (
          <div className="space-y-6">
            <CompanionProfileStatusIndicator
              userProfile={userProfile}
              onNavigateToVerification={() => {}}
            />
            <CompanionVerificationCenter
              companionId={(userProfile as any)?.uid || (userProfile?.email ? userProfile.email.replace(/[^a-zA-Z0-9]/g, '_') : 'CP1024')}
              companionName={userProfile?.displayName || userProfile?.name || 'Companion'}
              companionEmail={userProfile?.email || ''}
            />
          </div>
        )}

        {/* SECTION 4: PAIRING REQUESTS (DUAL-PAYMENT ENGINE) */}
        {activeSection === 'requests' && (
          <div className="space-y-6 animate-in fade-in duration-150">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <h1 className="text-2xl font-black text-white tracking-tight">Pairing Requests & Dual Payments</h1>
                <p className="text-xs text-slate-400">
                  SRD v1.0 Section 18 & 50: Pairings only activate when both parties have verified payment.
                </p>
              </div>

              <button
                type="button"
                onClick={loadPairings}
                className="px-3.5 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-xl text-xs font-bold transition-all cursor-pointer flex items-center gap-1.5"
              >
                <RotateCcw className={`w-3.5 h-3.5 ${isLoadingPairings ? 'animate-spin text-purple-400' : ''}`} />
                <span>Refresh</span>
              </button>
            </div>

            <div className="space-y-4">
              {pairings.length === 0 ? (
                <div className="p-8 text-center bg-slate-900 border border-slate-800 rounded-3xl text-slate-400 text-xs">
                  No pairing requests available.
                </div>
              ) : (
                pairings.map((p) => (
                  <div
                    key={p.id}
                    className="p-5 rounded-3xl bg-slate-900 border border-slate-800 shadow-xl space-y-4"
                  >
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-slate-800 pb-3">
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-black text-white text-base">{p.service_type}</h3>
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-purple-950 border border-purple-800 text-purple-300 font-mono font-bold">
                            {p.id}
                          </span>
                        </div>
                        <p className="text-xs text-slate-400 mt-0.5">
                          Requested by <strong className="text-white">{p.user_name}</strong> ({p.user_email})
                        </p>
                      </div>

                      <span
                        className={`px-3 py-1 rounded-full text-xs font-bold font-mono ${
                          p.pair_status === 'ACTIVE'
                            ? 'bg-emerald-950 border border-emerald-700 text-emerald-400'
                            : 'bg-amber-950 border border-amber-700 text-amber-400'
                        }`}
                      >
                        {p.pair_status === 'ACTIVE' ? '✓ PAIR ACTIVE' : '⏳ AWAITING DUAL PAYMENTS'}
                      </span>
                    </div>

                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs font-mono">
                      <div className="p-3 bg-slate-950 rounded-2xl border border-slate-800">
                        <span className="text-[10px] text-slate-500 uppercase block">Scheduled Date</span>
                        <strong className="text-white">{p.scheduled_date}</strong>
                      </div>
                      <div className="p-3 bg-slate-950 rounded-2xl border border-slate-800">
                        <span className="text-[10px] text-slate-500 uppercase block">Duration</span>
                        <strong className="text-white">{p.hours} Hours</strong>
                      </div>
                      <div className="p-3 bg-slate-950 rounded-2xl border border-slate-800">
                        <span className="text-[10px] text-slate-500 uppercase block">User Payment</span>
                        <strong className={p.user_payment_status === 'SUCCESS' ? 'text-emerald-400' : 'text-amber-400'}>
                          {p.user_payment_status === 'SUCCESS' ? '✓ Paid' : '⏳ Pending'}
                        </strong>
                      </div>
                      <div className="p-3 bg-slate-950 rounded-2xl border border-slate-800">
                        <span className="text-[10px] text-slate-500 uppercase block">Companion Fee</span>
                        <strong className={p.companion_payment_status === 'SUCCESS' ? 'text-emerald-400' : 'text-amber-400'}>
                          {p.companion_payment_status === 'SUCCESS' ? '✓ Paid (₹' + p.companion_fee + ')' : '⏳ Due (₹' + p.companion_fee + ')'}
                        </strong>
                      </div>
                    </div>

                    {/* Dual payment trigger action */}
                    {p.companion_payment_status !== 'SUCCESS' ? (
                      <div className="p-4 bg-gradient-to-r from-purple-950/60 to-slate-950 rounded-2xl border border-purple-800 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs">
                        <span className="text-slate-300">
                          Complete your companion verification fee of <strong>₹{p.companion_fee}</strong> to activate this pairing meetup.
                        </span>
                        <button
                          type="button"
                          onClick={() => handlePayCompanionSide(p.id)}
                          className="px-5 py-2.5 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white rounded-xl font-bold transition-all shadow-lg cursor-pointer shrink-0"
                        >
                          Verify & Pay ₹{p.companion_fee} Now
                        </button>
                      </div>
                    ) : (
                      <div className="p-3 bg-emerald-950/40 border border-emerald-800/60 rounded-2xl text-xs text-emerald-300 flex items-center gap-2">
                        <CheckCircle2 className="w-4 h-4 shrink-0 text-emerald-400" />
                        <span>Your companion fee has been verified server-side! {p.pair_status === 'ACTIVE' ? 'The meetup is active.' : 'Waiting for client payment to finalize.'}</span>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* SECTION 5: MESSAGES / CHAT */}
        {activeSection === 'messages' && (
          <div className="space-y-4 animate-in fade-in duration-150">
            <h1 className="text-2xl font-black text-white tracking-tight">Messages & Chat</h1>

            <div className="bg-slate-900 border border-slate-800 rounded-3xl p-5 shadow-2xl flex flex-col h-[520px]">
              {/* Chat Header */}
              <div className="flex items-center justify-between pb-3 border-b border-slate-800">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-blue-600 text-white font-bold flex items-center justify-center">
                    V
                  </div>
                  <div>
                    <h3 className="font-bold text-white text-xs">Vikram Malhotra (Client)</h3>
                    <span className="text-[10px] text-emerald-400 flex items-center gap-1">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                      Active Pairing • Park Street Meetup
                    </span>
                  </div>
                </div>
              </div>

              {/* Chat Messages Body */}
              <div className="flex-1 overflow-y-auto py-4 space-y-3">
                {chatMessages.map((msg) => (
                  <div
                    key={msg.id}
                    className={`flex flex-col ${msg.sender === 'me' ? 'items-end' : 'items-start'}`}
                  >
                    <div
                      className={`max-w-xs sm:max-w-md px-3.5 py-2.5 rounded-2xl text-xs leading-relaxed ${
                        msg.sender === 'me'
                          ? 'bg-purple-600 text-white rounded-br-none'
                          : 'bg-slate-800 text-slate-200 rounded-bl-none'
                      }`}
                    >
                      {msg.text}
                    </div>
                    <span className="text-[9px] text-slate-500 font-mono mt-1 px-1">{msg.time}</span>
                  </div>
                ))}
              </div>

              {/* Chat Input Form */}
              <form onSubmit={handleSendMessage} className="pt-3 border-t border-slate-800 flex items-center gap-2">
                <input
                  type="text"
                  value={newMsgText}
                  onChange={(e) => setNewMsgText(e.target.value)}
                  placeholder="Type a polite message to your client..."
                  className="flex-1 px-3.5 py-2 bg-slate-950 border border-slate-800 rounded-xl text-xs text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
                <button
                  type="submit"
                  className="p-2.5 bg-purple-600 hover:bg-purple-500 text-white rounded-xl transition-all cursor-pointer"
                >
                  <Send className="w-4 h-4" />
                </button>
              </form>
            </div>
          </div>
        )}

        {/* SECTION 6: AVAILABILITY */}
        {activeSection === 'availability' && (
          <div className="space-y-6 animate-in fade-in duration-150">
            <h1 className="text-2xl font-black text-white tracking-tight">Availability & Working Schedule</h1>

            <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 space-y-4 shadow-xl text-xs">
              <div className="space-y-2">
                <span className="font-bold text-white block">Active Working Days</span>
                <div className="flex items-center gap-2 flex-wrap">
                  {Object.entries(workingDays).map(([day, active]) => (
                    <button
                      key={day}
                      type="button"
                      onClick={() =>
                        setWorkingDays((prev) => ({ ...prev, [day]: !active }))
                      }
                      className={`px-4 py-2 rounded-xl font-mono font-bold transition-all cursor-pointer ${
                        active
                          ? 'bg-purple-600 text-white'
                          : 'bg-slate-950 border border-slate-800 text-slate-500'
                      }`}
                    >
                      {day} {active ? '✓' : ''}
                    </button>
                  ))}
                </div>
              </div>

              <div className="space-y-2 pt-3 border-t border-slate-800">
                <span className="font-bold text-white block">Working Hours</span>
                <input
                  type="text"
                  value={workingHours}
                  onChange={(e) => setWorkingHours(e.target.value)}
                  className="w-full sm:w-80 px-3 py-2 bg-slate-950 border border-slate-800 rounded-xl text-white font-mono"
                />
              </div>

              <button
                type="button"
                onClick={() => showToast('Availability schedule saved!', 'success')}
                className="px-5 py-2.5 bg-purple-600 hover:bg-purple-500 text-white rounded-xl font-bold cursor-pointer transition-all"
              >
                Save Availability
              </button>
            </div>
          </div>
        )}

        {/* SECTION 7: SETTINGS & VISIBILITY */}
        {activeSection === 'settings' && (
          <div className="space-y-6 animate-in fade-in duration-150">
            <h1 className="text-2xl font-black text-white tracking-tight">Companion Settings & Visibility</h1>

            <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 space-y-4 shadow-xl text-xs">
              <div className="space-y-2">
                <span className="font-bold text-white block">Profile Visibility in Discover</span>
                <div className="space-y-2">
                  {[
                    { val: 'Public', desc: 'Visible to all verified clients in public search & discover.' },
                    { val: 'Limited', desc: 'Visible only to clients you have previously matched or liked.' },
                    { val: 'Hidden', desc: 'Temporarily hidden from search (no new requests).' },
                  ].map((opt) => (
                    <label
                      key={opt.val}
                      className="p-3 bg-slate-950 border border-slate-800 rounded-2xl flex items-center gap-3 cursor-pointer hover:border-purple-600 transition-colors"
                    >
                      <input
                        type="radio"
                        name="visibility"
                        value={opt.val}
                        checked={profileVisibility === opt.val}
                        onChange={() => setProfileVisibility(opt.val as any)}
                        className="text-purple-600 focus:ring-purple-500 cursor-pointer"
                      />
                      <div>
                        <strong className="text-white block font-bold">{opt.val} Visibility</strong>
                        <span className="text-slate-400 text-[11px]">{opt.desc}</span>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              <button
                type="button"
                onClick={() => showToast(`Visibility preference saved to ${profileVisibility}!`, 'success')}
                className="px-5 py-2.5 bg-purple-600 hover:bg-purple-500 text-white rounded-xl font-bold cursor-pointer transition-all"
              >
                Update Visibility
              </button>
            </div>

            {/* Embedded Lifecycle & Status Manager */}
            <CompanionLifecycleManager userId={currentUser || 'comp-01'} />
          </div>
        )}

        {/* SECTION: COMPANION PROFILE LIFECYCLE & STATUS */}
        {activeSection === 'lifecycle' && (
          <div className="space-y-6 animate-in fade-in duration-150">
            <CompanionLifecycleManager userId={currentUser || 'comp-01'} />
          </div>
        )}

        {/* SECTION 8: SUPPORT & SAFETY */}
        {activeSection === 'support' && (
          <div className="space-y-6 animate-in fade-in duration-150">
            <h1 className="text-2xl font-black text-white tracking-tight">Companion Safety & Support</h1>

            <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 space-y-4 shadow-xl text-xs">
              <div className="p-4 bg-rose-950/40 border border-rose-800/60 rounded-2xl space-y-1">
                <div className="flex items-center gap-2 font-bold text-rose-300">
                  <ShieldAlert className="w-4 h-4 text-rose-400" />
                  <span>24/7 Companion Safety Protocol</span>
                </div>
                <p className="text-slate-300 leading-relaxed">
                  All meetups arranged via PairUpToday must take place in public venues (cafes, restaurants, art galleries, business events). You are never obligated to attend an uncomfortable location.
                </p>
              </div>

              <div className="p-4 bg-slate-950 border border-slate-800 rounded-2xl space-y-2">
                <span className="font-bold text-white block">Administrator Emergency Assistance</span>
                <p className="text-slate-400">
                  Direct support: <strong className="text-purple-300">support@pairuptoday.com</strong> • Priority Helpline: <strong className="text-purple-300 font-mono">+91 98300 00000</strong>
                </p>
              </div>
            </div>
          </div>
        )}

        {/* SECTION 9: COMPANION WALLET & EARNINGS (PAYMENTS) */}
        {activeSection === 'payments' && (
          <div className="space-y-6 animate-in fade-in duration-150">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <h1 className="text-2xl font-black text-white tracking-tight">
                  Companion Wallet &amp; Earnings
                </h1>
                <p className="text-xs text-slate-400 mt-0.5">
                  Real verified payouts and settlement ledger. All balances are calculated from completed bookings.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setActiveSection('verification')}
                className="px-4 py-2 bg-purple-600 hover:bg-purple-500 text-white rounded-xl text-xs font-bold transition-all cursor-pointer self-start sm:self-auto"
              >
                Manage Bank Account Details
              </button>
            </div>

            {/* Section 18: Database-Backed Wallet Metric Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3">
              <div className="p-4 rounded-2xl bg-slate-900 border border-slate-800 space-y-1">
                <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400 block">
                  Wallet balance (Available balance)
                </span>
                <span className="text-xl font-black text-emerald-400 font-mono block">
                  ₹{Number(payoutStats.availableBalance || 0).toLocaleString('en-IN')}
                </span>
                <span className="text-[10px] text-slate-500 block">Available balance cleared</span>
              </div>

              <div className="p-4 rounded-2xl bg-slate-900 border border-slate-800 space-y-1">
                <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400 block">
                  Amount earned
                </span>
                <span className="text-xl font-black text-purple-300 font-mono block">
                  ₹{Number((payoutStats as any).totalEarned ?? (Number(payoutStats.availableBalance || 0) + Number(payoutStats.totalPaidOut || 0))).toLocaleString('en-IN')}
                </span>
                <span className="text-[10px] text-slate-500 block">Total 85% companion share</span>
              </div>

              <div className="p-4 rounded-2xl bg-slate-900 border border-slate-800 space-y-1">
                <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400 block">
                  Amount deposited
                </span>
                <span className="text-xl font-black text-blue-400 font-mono block">
                  ₹{Number((userProfile as any)?.amountDeposited || 0).toLocaleString('en-IN')}
                </span>
                <span className="text-[10px] text-slate-500 block">Security & escrow deposits</span>
              </div>

              <div className="p-4 rounded-2xl bg-slate-900 border border-slate-800 space-y-1">
                <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400 block">
                  Withdrawable balance
                </span>
                <span className="text-xl font-black text-emerald-300 font-mono block">
                  ₹{Number(payoutStats.availableBalance || 0).toLocaleString('en-IN')}
                </span>
                <span className="text-[10px] text-slate-500 block">Eligible for bank payout</span>
              </div>

              <div className="p-4 rounded-2xl bg-slate-900 border border-slate-800 space-y-1">
                <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400 block">
                  Pending balance
                </span>
                <span className="text-xl font-black text-amber-400 font-mono block">
                  ₹{Number(payoutStats.pendingBalance || 0).toLocaleString('en-IN')}
                </span>
                <span className="text-[10px] text-slate-500 block">In escrow / pending OTP</span>
              </div>

              <div className="p-4 rounded-2xl bg-slate-900 border border-slate-800 space-y-1">
                <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400 block">
                  Withdrawn amount
                </span>
                <span className="text-xl font-black text-sky-400 font-mono block">
                  ₹{Number(payoutStats.totalPaidOut || 0).toLocaleString('en-IN')}
                </span>
                <span className="text-[10px] text-slate-500 block">Settled to verified bank account</span>
              </div>
            </div>

            {/* Transaction History */}
            <div className="p-6 rounded-3xl bg-slate-900 border border-slate-800 space-y-4">
              <div className="flex items-center justify-between border-b border-slate-800 pb-3">
                <h2 className="text-base font-black text-white">Transaction history</h2>
                <span className="text-xs font-mono text-slate-400">
                  {payoutStats.payouts.length} record(s)
                </span>
              </div>

              {payoutStats.payouts.length === 0 ? (
                <div className="py-10 text-center text-xs text-slate-400 font-semibold">
                  No transactions yet
                </div>
              ) : (
                <div className="space-y-2.5">
                  {payoutStats.payouts.map((p) => (
                    <div
                      key={p.id}
                      className="p-4 rounded-2xl bg-slate-950 border border-slate-800 flex flex-col sm:flex-row sm:items-center justify-between gap-2 text-xs"
                    >
                      <div>
                        <span className="font-bold text-white block">Booking #{p.booking_id}</span>
                        <span className="text-[11px] text-slate-400 font-mono">
                          Payout ID: {p.id} • {p.created_at}
                        </span>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="font-mono font-bold text-emerald-400 text-sm">
                          ₹{Number(p.payout_amount || 0).toLocaleString('en-IN')}
                        </span>
                        <span className="px-2.5 py-0.5 rounded-full bg-purple-950 border border-purple-800 text-purple-300 font-mono text-[10px] font-bold">
                          {p.status}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Other sections fallback */}
        {(activeSection === 'matches' || activeSection === 'notifications') && (
          <div className="space-y-6 animate-in fade-in duration-150">
            <h1 className="text-2xl font-black text-white tracking-tight capitalize">{activeSection}</h1>
            <div className="bg-slate-900 border border-slate-800 rounded-3xl p-8 text-center text-xs text-slate-400 space-y-2">
              <p>Everything in your <strong>{activeSection}</strong> is up to date.</p>
              <button
                type="button"
                onClick={() => setActiveSection('dashboard')}
                className="px-4 py-2 bg-purple-600 text-white rounded-xl font-bold cursor-pointer"
              >
                Back to Dashboard
              </button>
            </div>
          </div>
        )}

        {/* Booking Chat Modal */}
        {activeChatBooking && (
          <BookingChatModal
            booking={activeChatBooking}
            currentUserId={(userProfile as any)?.uid || currentUser || ''}
            currentUserName={userProfile?.displayName || userProfile?.name || 'Companion'}
            currentUserRole="companion"
            onClose={() => setActiveChatBooking(null)}
          />
        )}

        {/* Meeting Start OTP Modal ("Let's Start Meeting" -> "Enter Your Meeting Start OTP") */}
        {activeStartOtpBooking && (
          <div className="fixed inset-0 z-50 bg-slate-950/80 backdrop-blur-xs flex items-center justify-center p-4 animate-in fade-in">
            <div className="bg-slate-900 border border-slate-800 rounded-3xl max-w-md w-full p-6 space-y-4 shadow-2xl">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Clock className="w-5 h-5 text-emerald-400" />
                  <h3 className="text-base font-black text-white">Let&apos;s Start Meeting</h3>
                </div>
                <button
                  type="button"
                  onClick={() => setActiveStartOtpBooking(null)}
                  className="p-1.5 rounded-full bg-slate-800 text-slate-400 hover:text-white cursor-pointer"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              <p className="text-xs text-slate-300 leading-relaxed">
                A 6-digit Meeting Start OTP has been sent to <strong>{activeStartOtpBooking.client_name}</strong> via APITXT SMS. Ask the client for the code and enter it below to start the live meeting countdown.
              </p>

              {startOtpNotice && (
                <div className="p-3 rounded-xl bg-emerald-950/80 border border-emerald-800 text-emerald-200 text-xs">
                  {startOtpNotice}
                </div>
              )}

              {startOtpError && (
                <div className="p-3 rounded-xl bg-rose-950/80 border border-rose-800 text-rose-200 text-xs font-bold">
                  {startOtpError}
                </div>
              )}

              <form onSubmit={handleVerifyStartOtpSubmit} className="space-y-3">
                <div>
                  <label className="block text-xs font-bold text-slate-200 mb-1.5">
                    Enter Your Meeting Start OTP
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    enterKeyHint="done"
                    maxLength={6}
                    value={startOtpInput}
                    onChange={(e) => setStartOtpInput(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="Enter 6-digit Start OTP"
                    className="w-full px-4 py-3 rounded-xl bg-slate-950 border border-slate-700 text-white font-mono text-base tracking-widest text-center focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  />
                </div>
                <div className="flex gap-2">
                  <button
                    type="submit"
                    disabled={isVerifyingStartOtp || startOtpInput.length !== 6}
                    className="flex-1 py-3 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white font-bold text-xs cursor-pointer disabled:opacity-50"
                  >
                    {isVerifyingStartOtp ? 'Verifying...' : 'Verify Start OTP & Start Countdown'}
                  </button>
                  <button
                    type="button"
                    onClick={() => void handleOpenStartMeetingModal(activeStartOtpBooking)}
                    className="px-3.5 py-3 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-200 font-bold text-xs cursor-pointer"
                  >
                    Resend
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Slide-to-Complete Meeting Completion OTP Modal */}
        {activeOtpBooking && (
          <div className="fixed inset-0 z-50 bg-slate-950/80 backdrop-blur-xs flex items-center justify-center p-4 animate-in fade-in">
            <div className="bg-slate-900 border border-slate-800 rounded-3xl max-w-md w-full p-6 space-y-4 shadow-2xl">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <KeyRound className="w-5 h-5 text-pink-400" />
                  <h3 className="text-base font-black text-white">Complete Meeting &amp; Settlement</h3>
                </div>
                <button
                  type="button"
                  onClick={() => setActiveOtpBooking(null)}
                  className="p-1.5 rounded-full bg-slate-800 text-slate-400 hover:text-white cursor-pointer"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              <p className="text-xs text-slate-300 leading-relaxed">
                Slide the control below to initiate meeting completion for <strong>#{activeOtpBooking.id}</strong>. This sends a new 6-digit Meeting Completion OTP to <strong>{activeOtpBooking.client_name}</strong> via APITXT SMS.
              </p>

              {/* Deliberate Slide-to-Complete Control */}
              <div className="p-4 rounded-2xl bg-slate-950 border border-slate-800 space-y-2">
                <div className="flex items-center justify-between text-xs font-bold">
                  <span className={slideToCompleteConfirmed ? 'text-emerald-400' : 'text-pink-400'}>
                    {slideToCompleteConfirmed
                      ? '✓ Slide Confirmed — Completion OTP Sent to Client'
                      : 'Slide to Complete Meeting →'}
                  </span>
                  <span className="font-mono text-slate-400">{slideToCompleteProgress}%</span>
                </div>
                <input
                  type="range"
                  min={0}
                  max={100}
                  value={slideToCompleteProgress}
                  disabled={slideToCompleteConfirmed}
                  onChange={(e) => void handleSlideCompleteChange(Number(e.target.value))}
                  className="w-full accent-pink-500 cursor-pointer"
                />
              </div>

              {otpVerifyError && (
                <div className="p-3 rounded-xl bg-rose-950/80 border border-rose-800 text-rose-200 text-xs font-bold">
                  {otpVerifyError}
                </div>
              )}

              {otpVerifySuccess ? (
                <div className="p-4 rounded-2xl bg-emerald-950/80 border border-emerald-700 text-emerald-200 text-xs space-y-2 text-center">
                  <CheckCircle2 className="w-8 h-8 text-emerald-400 mx-auto" />
                  <p className="font-black text-base text-emerald-300">Meeting Successful</p>
                  <p className="font-bold text-xs text-emerald-200">Meeting Verified &amp; Completed!</p>
                  <p>₹{otpVerifySuccess.amount} companion payout has been credited to your wallet.</p>
                  <button
                    type="button"
                    onClick={() => setActiveOtpBooking(null)}
                    className="w-full py-2.5 rounded-xl bg-emerald-600 text-white font-bold text-xs cursor-pointer"
                  >
                    Done
                  </button>
                </div>
              ) : (
                slideToCompleteConfirmed && (
                  <form onSubmit={handleVerifyOtpSubmit} className="space-y-3">
                    <div>
                      <label className="block text-xs font-bold text-slate-200 mb-1.5">
                        Enter 6-Digit Meeting Completion OTP
                      </label>
                      <input
                        type="text"
                        inputMode="numeric"
                        enterKeyHint="done"
                        maxLength={6}
                        value={completionOtpInput}
                        onChange={(e) => setCompletionOtpInput(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        placeholder="Enter 6-digit Completion OTP"
                        className="w-full px-4 py-3 rounded-xl bg-slate-950 border border-slate-700 text-white font-mono text-base tracking-widest text-center focus:outline-none focus:ring-2 focus:ring-pink-500"
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={isVerifyingOtp || completionOtpInput.length !== 6}
                      className="w-full py-3 rounded-xl bg-gradient-to-r from-pink-600 to-rose-600 hover:from-pink-500 hover:to-rose-500 text-white font-bold text-xs cursor-pointer disabled:opacity-50"
                    >
                      {isVerifyingOtp ? 'Verifying Completion OTP...' : 'Verify Completion OTP & Release Settlement'}
                    </button>
                  </form>
                )
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
};
